import Scene from './Scene.js';
import {
    setScenes,
    setCurrentScene,
} from 'actions/index.js';
import {moveKey} from 'utils/objectMover.js';
import {
    STAY,
    FORWARD,
    REVERSE,
    FORWARD_REVERSE,
    BROWNIAN
} from 'constants/scene-algorithms.js';

class Scenes {
    constructor(sequencerEngine) {
        this.sequencerEngine = sequencerEngine;
        this.scenes = [];
    }

    get subSequences() {
        return this.sequencerEngine.subSequences;
    }

    get state() {
        return this.sequencerEngine.state;
    }

    newScene() {
        // find the current pattern for each sub sequence
        let name = 'Scene ' + (this.scenes.length + 1);

        let patterns = [];
        for (let subSequence of this.subSequences) {
            patterns.push(subSequence.tracks[0].currentPattern);
        }

        let scene = new Scene(name, patterns);
        this.scenes = [... this.scenes, scene];

        this.sequencerEngine.dispatch(setScenes(this.scenes));
        this.sequencerEngine.dispatch(setCurrentScene(scene));
    }

    moveScene(fromIndex, toIndex) {
        let scenes = moveKey(fromIndex, toIndex, this.scenes);
        this.scenes = Object.keys(scenes).map(i => scenes[i]);
        this.sequencerEngine.dispatch(setScenes([... this.scenes]));
    }

    deleteSubSequence(subSequenceNumber) {
        this.scenes.forEach(
            s => s.patterns.splice(subSequenceNumber, 1));
        this.scenes.forEach(
            s => s.algorithmTypes.splice(subSequenceNumber, 1));
        this.scenes.forEach(
            s => s.durationPercentages.splice(subSequenceNumber, 1));
    }

    newSubSequence() {
        this.scenes.forEach(
            s => s.patterns.push(0));
        this.scenes.forEach(
            s => s.algorithmTypes.push(STAY));
        this.scenes.forEach(
            s => s.durationPercentages.push(100));
    }

    deleteScene(scene) {
        let index = this.scenes.indexOf(scene);
        this.scenes.splice(index, 1);
        this.sequencerEngine.dispatch(setScenes([... this.scenes]));
        this.sequencerEngine.dispatch(setCurrentScene(undefined));
    }

    switchToScene(scene) {
        if (!scene) {
            return;
        }
        for (let i in this.subSequences) {
            let subSequence = this.subSequences[i];
            let patternNumber = scene.patterns[i];
            this.sequencerEngine.switchToPattern(patternNumber, false, subSequence);
        }

        this.sequencerEngine.dispatch(setCurrentScene(scene));
    }

    get currentScene() {
        return this.state.sequencer.currentScene;
    }

    onNewStep(stepNumber) {
        // called on every new step
        // we check what the current scene is, and run the algo
        // for that scene if any

        let currentScene = this.currentScene;
        if (currentScene === undefined || stepNumber <= 1) {
            // do nothing when theres no scene
            return;
        }

        for (let i in currentScene.algorithmTypes) {
            let subSequence = this.subSequences[i];
            let type = currentScene.algorithmTypes[i];
            let duration = currentScene.durationPercentages[i];
            let length = Math.round(subSequence.length * duration / 100.0);

            // check if length matches
            if (stepNumber % length !== 0 || !subSequence.tracks[0]) {
                // we dont need to run algo on this step
                continue;
            }

            let currentPattern = subSequence.tracks[0].currentPattern;
            let numPatterns = subSequence.tracks[0].patterns.length;

            switch (type) {
            case FORWARD:
                // we move forward one step
                this.sequencerEngine.switchToPattern(
                    (currentPattern + 1) % numPatterns,
                    false,
                    subSequence,
                    true);
                continue;
            case REVERSE:
                currentPattern = currentPattern - 1;
                if (currentPattern < 0) {
                    currentPattern = numPatterns - 1;
                }
                this.sequencerEngine.switchToPattern(
                    currentPattern,
                    false,
                    subSequence,
                    true);
                continue;
            case BROWNIAN:
                let flip = Math.random();
                if (flip < 0.5) {
                    // then we stay
                    continue;
                } else if (flip < 0.75) {
                    // we go forward
                    this.sequencerEngine.switchToPattern(
                        (currentPattern + 1) % numPatterns,
                        false,
                        subSequence,
                        true
                    );
                } else {
                    // we go backwards 
                    currentPattern = currentPattern - 1;
                    if (currentPattern < 0) {
                        currentPattern = numPatterns - 1;
                    }
                    this.sequencerEngine.switchToPattern(
                        currentPattern,
                        false,
                        subSequence,
                        true
                    );
                }
            }
        }
    }

    getJson() {
        return this.scenes.map(x => x.getJson());
    }

    fromJson(json) {
        for (let s of json) {
            let scene = new Scene(s.name, s.patterns);
            if (s.algorithmTypes) {
                scene.algorithmTypes = s.algorithmTypes;
            }
            if (s.durationPercentages) {
                scene.durationPercentages = s.durationPercentages;
            }
            this.scenes.push(scene);
        }
        this.sequencerEngine.dispatch(setScenes([... this.scenes]));
    }
}

export default Scenes;
